import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import AdminCantonOverview from './AdminCantonOverview';
import AdminCantonDetail from './AdminCantonDetail';
import AdminDivisionDetail from './AdminDivisionDetail';
import DivisionLockSystem from './DivisionLockSystem';

const Tabs = styled.div`
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 1.25rem;
  gap: 0.25rem;
`;

const Tab = styled.button`
  padding: 0.75rem 1.25rem;
  border: none;
  background: none;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${(p) => (p.$active ? '#043663' : '#6b7280')};
  border-bottom: 2px solid ${(p) => (p.$active ? '#043663' : 'transparent')};
  margin-bottom: -2px;
  cursor: pointer;

  &:hover {
    color: #043663;
  }
`;

/**
 * Divisions hub: canton overview + Division Lock (nested; was a top-level nav item).
 * @param {{
 *   adminUser?: string,
 *   canReadDivisionLock?: boolean,
 *   initialTab?: 'overview' | 'lock',
 * }} props
 */
const AdminDivisionsSection = ({
  adminUser = 'admin',
  canReadDivisionLock = false,
  initialTab = 'overview',
} = {}) => {
  const [hubTab, setHubTab] = useState(initialTab === 'lock' && canReadDivisionLock ? 'lock' : 'overview');
  const [currentView, setCurrentView] = useState('overview'); // 'overview', 'canton', or 'division'
  const [selectedCanton, setSelectedCanton] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState('');

  useEffect(() => {
    if (initialTab === 'lock' && canReadDivisionLock) {
      setHubTab('lock');
    }
  }, [initialTab, canReadDivisionLock]);

  const handleCantonSelect = (canton) => {
    setSelectedCanton(canton);
    setCurrentView('canton');
  };

  const handleDivisionSelect = (division) => {
    setSelectedDivision(division);
    setCurrentView('division');
  };

  const handleBackToCanton = () => {
    setCurrentView('canton');
    setSelectedDivision(null);
  };

  const handleBackToOverview = () => {
    setCurrentView('overview');
    setSelectedCanton(null);
    setSelectedDivision(null);
  };

  const showLockTab = !!canReadDivisionLock;

  return (
    <div>
      {showLockTab && (
        <Tabs>
          <Tab
            type="button"
            $active={hubTab === 'overview'}
            onClick={() => setHubTab('overview')}
          >
            Overview
          </Tab>
          <Tab
            type="button"
            $active={hubTab === 'lock'}
            onClick={() => setHubTab('lock')}
          >
            Division Lock
          </Tab>
        </Tabs>
      )}

      {hubTab === 'lock' && showLockTab ? (
        <DivisionLockSystem adminUser={adminUser} />
      ) : currentView === 'division' ? (
        <AdminDivisionDetail
          division={selectedDivision}
          seasonId={selectedDivision?.seasonId || selectedSeason}
          onBack={handleBackToCanton}
        />
      ) : currentView === 'canton' ? (
        <AdminCantonDetail
          canton={selectedCanton}
          seasonId={selectedSeason}
          onBack={handleBackToOverview}
          onDivisionSelect={handleDivisionSelect}
        />
      ) : (
        <AdminCantonOverview
          onCantonSelect={handleCantonSelect}
          selectedSeason={selectedSeason}
          onSeasonChange={setSelectedSeason}
        />
      )}
    </div>
  );
};

export default AdminDivisionsSection;
